import { motion } from 'motion/react';
import { start, contact } from '../../../content/agencyCopy';
import RevealOnScroll from '../RevealOnScroll';
import SignalCta from '../SignalCta';

/*
 * The section that stops a business owner from feeling out of place.
 *
 * Everyone arriving here is in one of two states — they know what they want
 * built, or they only know something should change. Both get a door, neither
 * door asks for money, and both converge on the same paid engagement (the
 * Audit) which is drawn underneath as the shared next step.
 */

function PathCard({ p, index }) {
  const isAssess = p.ctaKind === 'assess';
  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.55, delay: index * 0.1, ease: [0.2, 0.7, 0.2, 1] }}
      className="fa-plate fa-plate--interactive fa-ticks p-6 md:p-8 flex flex-col"
    >
      <div className="flex items-center justify-between">
        <span className="fa-ref fa-ref--signal">{p.ref}</span>
        <span className="fa-ref" style={{ fontSize: 9 }}>No cost</span>
      </div>

      <p className="fa-label mt-5" style={{ color: 'var(--fa-muted)' }}>{p.eyebrow}</p>
      <h3
        className="font-heading text-[24px] md:text-[27px] font-semibold mt-2 leading-snug"
        style={{ color: 'var(--fa-ink)', maxWidth: '18ch' }}
      >
        {p.title}
      </h3>
      <p className="mt-4 text-[15px] fa-glow-text fa-glow-text--muted" style={{ maxWidth: '44ch' }}>
        {p.body}
      </p>

      <ol className="mt-6 space-y-2.5 flex-1">
        {p.steps.map((s, i) => (
          <li key={s} className="flex items-start gap-3">
            <span
              className="fa-ref flex-shrink-0 pt-[3px]"
              style={{ color: 'var(--fa-signal)', fontSize: 9.5 }}
              aria-hidden="true"
            >
              {String(i + 1).padStart(2, '0')}
            </span>
            <span className="text-[13.5px] fa-glow-text fa-glow-text--muted">{s}</span>
          </li>
        ))}
      </ol>

      <div className="mt-8">
        {isAssess ? (
          <SignalCta to="/assess">{p.cta}</SignalCta>
        ) : (
          <SignalCta
            data-cal-link={contact.calLink}
            data-cal-namespace={contact.calNamespace}
          >
            {p.cta}
          </SignalCta>
        )}
      </div>
    </motion.div>
  );
}

export default function Start() {
  return (
    <section
      id="start"
      className="fa-grid px-6 py-24 md:py-32"
      style={{ background: 'var(--fa-surface)', borderTop: '1px solid var(--fa-rule)' }}
    >
      <RevealOnScroll className="max-w-[1120px] mx-auto">
        <div className="fa-label fa-header-rule mb-5">
          <span className="flex items-center gap-3">
            <span className="fa-status-dot fa-status-dot--live" aria-hidden="true" />
            {start.label}
          </span>
        </div>
        <h2
          className="font-heading text-[30px] md:text-[42px] font-semibold"
          style={{ maxWidth: '20ch', color: 'var(--fa-ink)', textWrap: 'balance' }}
        >
          {start.headline}
        </h2>
        <p className="mt-5 text-[16px] fa-glow-text fa-glow-text--muted" style={{ maxWidth: '52ch' }}>
          {start.intro}
        </p>

        <div className="mt-12 grid gap-5 md:grid-cols-2">
          {start.paths.map((p, i) => <PathCard key={p.key} p={p} index={i} />)}
        </div>

        {/* Connector — both paths converge here */}
        <div className="flex flex-col items-center" aria-hidden="true">
          <motion.span
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            style={{
              width: 1, height: 44, background: 'var(--fa-signal)',
              opacity: 0.5, transformOrigin: 'top', display: 'block',
            }}
          />
          <span
            style={{
              width: 6, height: 6, borderRadius: '50%',
              background: 'var(--fa-signal)', display: 'block', marginTop: -1,
            }}
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.55, ease: [0.2, 0.7, 0.2, 1] }}
          className="fa-plate fa-ticks mt-4 p-6 md:p-10"
          style={{ background: 'var(--fa-paper)', borderColor: 'var(--fa-signal)' }}
        >
          <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-start">
            <div className="md:col-span-3">
              <span className="fa-ref fa-ref--signal">{start.bridge.ref}</span>
              <h3
                className="font-display text-[26px] md:text-[30px] mt-3"
                style={{ color: 'var(--fa-ink)' }}
              >
                {start.bridge.title}
              </h3>
              <div className="mt-5 flex items-center gap-2 max-w-[190px]">
                <span className="fa-dim__cap" aria-hidden="true" />
                <span className="fa-dim flex-1"><span>0–2 days</span></span>
                <span className="fa-dim__cap" aria-hidden="true" />
              </div>
            </div>
            <div className="md:col-span-9">
              <p className="text-[15.5px] md:text-[16px] fa-glow-text" style={{ maxWidth: '64ch' }}>
                {start.bridge.body}
              </p>
              <p
                className="fa-leader mt-6"
                style={{ color: 'var(--fa-ink)', textTransform: 'none', letterSpacing: '0.01em', fontSize: '13px' }}
              >
                {start.bridge.outcome}
              </p>
            </div>
          </div>
        </motion.div>
      </RevealOnScroll>
    </section>
  );
}
